import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ArrowRight, CalendarOff } from "lucide-react";

import { UIButton, UICard, UIChip, UITable } from "../../ui";
import DashboardEmptyState from "../DashboardEmptyState";
import EntityCell from "../EntityCell";
import { STATUS_CHIP_COLOR } from "./constants";
import "../dashboardSection.scss";
import "./TopEventsTable.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");

/**
 * Dashboard top-events section — the three events with the most accepted
 * applicants over the last 30 days, or an empty state. Passing a
 * `columns.companies` label adds the owning-company column (admin dashboard);
 * without it the card is the company-portal "by Activity" variant.
 */
const TopEventsTable = ({
  title,
  viewEventsLabel,
  columns,
  statusLabels,
  rows,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onViewEvents,
  onEmptyAction,
  onRowClick,
  onCompanyClick,
}) => {
  const tableColumns = useMemo(
    () => [
      {
        field: "rank",
        // Empty headerName + autoFlex:false opt out of UITable's auto-sizing
        // (which forces a 130px min); the "#" label comes back via renderHeader.
        headerName: "",
        renderHeader: () => columns.rank,
        width: 45,
        autoFlex: false,
        headerClassName: "top-events__rank",
        cellClassName: "top-events__rank",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <span className="dashboard-rank">{row.rank}</span>
        ),
      },
      {
        field: "event",
        headerName: columns.event,
        flex: 2,
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <EntityCell
            variant="event"
            name={row.name}
            imageUrl={row.imageUrl}
            autoDirection
          />
        ),
      },
      ...(columns.companies
        ? [
            {
              field: "company",
              headerName: columns.companies,
              flex: 2,
              minWidth: 170,
              sortable: false,
              filterable: false,
              renderCell: ({ row }) => {
                const cell = (
                  <EntityCell
                    variant="company"
                    className="top-events__company"
                    name={row.company.name}
                    subtitle={row.company.activeSince}
                    imageUrl={row.company.imageUrl}
                  />
                );
                if (!onCompanyClick) return cell;

                // Opens the company instead of the row's event.
                const openCompany = (event) => {
                  event.stopPropagation();
                  onCompanyClick(row.company);
                };
                return (
                  <span
                    role="link"
                    tabIndex={0}
                    className="top-events__company-link"
                    onClick={openCompany}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") openCompany(event);
                    }}
                  >
                    {cell}
                  </span>
                );
              },
            },
          ]
        : []),
      {
        field: "applicants",
        headerName: columns.applicants,
        flex: 1,
        minWidth: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <span className="top-events__count">{formatCount(row.applicants)}</span>
        ),
      },
      {
        field: "status",
        headerName: columns.status,
        flex: 1,
        minWidth: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <UIChip
            appearance="light"
            size="small"
            color={STATUS_CHIP_COLOR[row.status] || "primary"}
            label={statusLabels[row.status] || row.status}
          />
        ),
      },
    ],
    [columns, statusLabels, onCompanyClick],
  );

  const viewEventsAction = (
    <UIButton
      variant="text"
      endIcon={<ArrowRight size={16} strokeWidth={2} />}
      className="dashboard-link"
      onClick={onViewEvents}
    >
      {viewEventsLabel}
    </UIButton>
  );

  return (
    <UICard
      title={title}
      action={viewEventsAction}
      className="dashboard-card top-events"
    >
      {rows.length > 0 ? (
        <UITable
          className="dashboard-table"
          rows={rows}
          columns={tableColumns}
          contentSized
          hideFooter
          striped={false}
          disableSelectionOnClick
          rowHeight={52}
          onRowClick={(params) => onRowClick(params.row)}
        />
      ) : (
        <DashboardEmptyState
          icon={<CalendarOff />}
          title={emptyTitle}
          description={emptyDescription}
          actionLabel={emptyActionLabel}
          onAction={onEmptyAction}
        />
      )}
    </UICard>
  );
};

TopEventsTable.propTypes = {
  title: PropTypes.string.isRequired,
  viewEventsLabel: PropTypes.string.isRequired,
  columns: PropTypes.shape({
    rank: PropTypes.string,
    event: PropTypes.string,
    companies: PropTypes.string,
    applicants: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  statusLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      rank: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      // Required only when `columns.companies` is passed.
      company: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        imageUrl: PropTypes.string,
        activeSince: PropTypes.string,
      }),
      applicants: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ),
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  emptyActionLabel: PropTypes.string,
  onViewEvents: PropTypes.func.isRequired,
  onEmptyAction: PropTypes.func,
  onRowClick: PropTypes.func.isRequired,
  /** Admin variant: clicking the Company cell opens that company (called with the row's `company`). */
  onCompanyClick: PropTypes.func,
};

TopEventsTable.defaultProps = {
  rows: [],
  emptyActionLabel: "",
  onEmptyAction: undefined,
  onCompanyClick: undefined,
};

export default TopEventsTable;
